import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams, useSearchParams } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import i18n from '../../i18n';
import oenotheraAutoRenewalEn from '../../terms-documents/Oenothera_auto_renewal_en_latest.md';
import oenotheraAutoRenewal from '../../terms-documents/Oenothera_auto_renewal_zh_latest.md';
import oenotheraPrivacyPolicyEn from '../../terms-documents/Oenothera_privacy_policy_en_latest.md';
import oenotheraPrivacyPolicy from '../../terms-documents/Oenothera_privacy_policy_zh_latest.md';
import oenotheraMembershipServiceEn from '../../terms-documents/Oenothera_membership_service_en_latest.md';
import oenotheraMembershipService from '../../terms-documents/Oenothera_membership_service_zh_latest.md';
import oenotheraUserTermsEn from '../../terms-documents/Oenothera_user_terms_en_latest.md';
import oenotheraUserTerms from '../../terms-documents/Oenothera_user_terms_zh_latest.md';
import './TermsPage.scss';

const TERMS_DOCUMENTS = {
  user: {
    zh: {
      title: '月见半夏用户协议',
      filePath: oenotheraUserTerms,
    },
    en: {
      title: 'Oenothera User Agreement',
      filePath: oenotheraUserTermsEn,
    },
  },
  privacy: {
    zh: {
      title: '月见半夏隐私政策',
      filePath: oenotheraPrivacyPolicy,
    },
    en: {
      title: 'Oenothera Privacy Policy',
      filePath: oenotheraPrivacyPolicyEn,
    },
  },
  membership: {
    zh: {
      title: '月见半夏会员服务协议',
      filePath: oenotheraMembershipService,
    },
    en: {
      title: 'Oenothera Membership Service Agreement',
      filePath: oenotheraMembershipServiceEn,
    },
  },
  'auto-renewal': {
    zh: {
      title: '月见半夏自动续费协议',
      filePath: oenotheraAutoRenewal,
    },
    en: {
      title: 'Oenothera Auto-Renewal Agreement',
      filePath: oenotheraAutoRenewalEn,
    },
  },
};

const NOT_FOUND_DOCUMENT = {
  zh: {
    title: '文档不存在',
    content: '## 文档不存在\n\n你访问的协议暂未发布或链接有误。',
  },
  en: {
    title: 'Document Not Found',
    content: '## Document Not Found\n\nThe agreement you are visiting has not been published or the link is invalid.',
  },
};

function normalizeLanguage(value) {
  if (!value) {
    return '';
  }

  const normalizedValue = value.toLowerCase();

  if (normalizedValue.startsWith('zh') || normalizedValue === 'cn' || normalizedValue === 'chinese') {
    return 'zh';
  }

  if (normalizedValue.startsWith('en') || normalizedValue === 'english') {
    return 'en';
  }

  return '';
}

function TermsPage() {
  const { type } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [markdownContent, setMarkdownContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const currentLanguage = useMemo(() => {
    const urlLanguage = normalizeLanguage(
      searchParams.get('lang') || searchParams.get('language') || searchParams.get('locale')
    );

    if (urlLanguage) {
      return urlLanguage;
    }

    return normalizeLanguage(i18n.resolvedLanguage || i18n.language || navigator.language) || 'zh';
  }, [searchParams]);

  const documentConfig = useMemo(() => {
    const documentByLanguage = TERMS_DOCUMENTS[type] || NOT_FOUND_DOCUMENT;
    return documentByLanguage[currentLanguage] || documentByLanguage.zh || documentByLanguage.en;
  }, [currentLanguage, type]);

  const languageToggleLabel = useMemo(() => {
    return currentLanguage === 'zh' ? 'English' : '中文';
  }, [currentLanguage]);

  const handleToggleLanguage = () => {
    const nextLanguage = currentLanguage === 'zh' ? 'en' : 'zh';
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete('language');
    nextSearchParams.delete('locale');
    nextSearchParams.set('lang', nextLanguage);
    setSearchParams(nextSearchParams);
  };

  useEffect(() => {
    const abortController = new AbortController();

    async function loadDocument() {
      setIsLoading(true);
      setLoadError('');

      if (documentConfig.content) {
        setMarkdownContent(documentConfig.content);
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(documentConfig.filePath, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load document: ${response.status}`);
        }

        const text = await response.text();
        setMarkdownContent(text);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setLoadError(currentLanguage === 'zh' ? '协议内容加载失败，请刷新页面重试。' : 'Failed to load the agreement. Please refresh and try again.');
          setMarkdownContent('');
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    loadDocument();

    return () => {
      abortController.abort();
    };
  }, [currentLanguage, documentConfig]);

  const renderDocumentContent = useMemo(() => {
    if (isLoading) {
      return <div className="terms-document-page__state">加载中...</div>;
    }

    if (loadError) {
      return <div className="terms-document-page__state terms-document-page__state--error">{loadError}</div>;
    }

    return (
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {markdownContent}
      </ReactMarkdown>
    );
  }, [isLoading, loadError, markdownContent]);

  return (
    <main className="terms-document-page">
      <header className="terms-document-page__header">
        <div className="terms-document-page__actions">
          <button className="terms-document-page__language" type="button" onClick={handleToggleLanguage}>
            {languageToggleLabel}
          </button>
        </div>
      </header>

      <article className="terms-document-page__card" aria-label={documentConfig.title}>
        <div className="terms-document-page__content">
          {renderDocumentContent}
        </div>
      </article>
    </main>
  );
}

export default TermsPage;
